import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Eye } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { STATUS_CONFIG } from './utils';
import OrderPopup from './OrderPopup';
import BranchToggle from './BranchToggle';

export default function OrdersTab() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewOrder, setViewOrder] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const limit = 15;
  const isSA = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (isSA) api.get('/admin/branches').then(r => setBranches(r.data.data || [])).catch(() => {});
  }, [isSA]);

  // Debounce user typing → actual query used for API
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [branchId, debouncedSearch]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (branchId) params.append('branch_id', branchId);
    if (debouncedSearch) params.append('q', debouncedSearch);
    api.get(`/admin/orders?${params.toString()}`).then(r => {
      const d = r.data.data || {};
      setOrders(d.orders || []);
      setTotal(d.total || 0);
    }).catch(() => {});
  }, [page, debouncedSearch, branchId]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const openView = async (id) => {
    try { const r = await api.get(`/admin/orders/${id}`); setViewOrder(r.data.data); }
    catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="orders-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {isSA && branches.length > 0 && (
          <BranchToggle branches={branches} value={branchId} onChange={setBranchId} testIdPrefix="orders-branch" />
        )}
        <Input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search order number, name, phone..."
          className="max-w-sm"
          data-testid="order-search"
        />
        <span className="text-xs text-muted-foreground ml-auto" data-testid="orders-total">
          {total} {total === 1 ? 'result' : 'results'}
        </span>
      </div>
      <div className="space-y-2">
        {orders.map(order => {
          const cfg = STATUS_CONFIG[order.status] || { label: order.status, color: 'bg-muted' };
          return (
            <div key={order.id} className="flex items-center justify-between border rounded-xl px-4 py-3" data-testid={`order-${order.id}`}>
              <div>
                <p className="text-sm font-bold">{order.order_number}</p>
                <p className="text-xs text-muted-foreground">
                  {order.customer_name || '-'} &middot; {order.category_name || '-'} &middot; {order.total_amount ? `Rs.${order.total_amount}` : 'TBD'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {order.sub_status === 'ALTERATION' && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600">Alt</span>}
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${cfg.color}`}>{cfg.label}</span>
                <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => openView(order.id)} data-testid={`view-order-${order.id}`}>
                  <Eye className="w-3 h-3" /> View
                </Button>
              </div>
            </div>
          );
        })}
        {orders.length === 0 && <p className="text-sm text-muted-foreground">No orders found.</p>}
      </div>
      {total > 0 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-border/50">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-xs h-7" data-testid="orders-prev-btn">Prev</Button>
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="orders-pagination">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs h-7" data-testid="orders-next-btn">Next</Button>
        </div>
      )}
      {viewOrder && <OrderPopup order={viewOrder} onClose={() => setViewOrder(null)} onRefresh={load} role={user?.role} />}
    </div>
  );
}
